import { usePostHog } from '@posthog/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { APIKey, IAPIKey, TAPIKeyScope } from '@feathr/blackbox';
import { APIKeyServiceNames } from '@feathr/blackbox';
import { showError, submitModelWithErrorHandling } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import { getExpirationAnalytics, validateAPIKeyName } from './CreateAPIKeyModal.utils';
import { selectedScopesToArray } from './ScopePicker/ScopePicker.utils';

export interface IUseCreateAPIKeyOptions {
  /** Service (Feathr API) success — hands off the one-time secret to the secret step. */
  onServiceKeyCreated: (secret: string) => void;
  /** Legacy (Zapier) success — the key is recoverable from the list, so just close. */
  onLegacyKeyCreated: () => void;
  /** Expiry for the default preset, so leaving the default still sends expires_at. */
  initialExpiresAt?: string;
  /** Shared "now" the expiry dates were computed from; reused for expiration_days so both agree. */
  reference: Date;
}

export interface IUseCreateAPIKey {
  serviceName: APIKeyServiceNames;
  name: string;
  /** Constraint violations for the current name; empty when the name is valid. */
  nameValidationErrors: string[];
  /**
   * Everything blocking the create: the name errors plus missing scope /
   * incomplete expiration (service keys only). Shown on the Create button's
   * disabled tooltip; empty exactly when `canCreate` is true.
   */
  createValidationErrors: string[];
  selectedScopes: Set<TAPIKeyScope>;
  isLegacy: boolean;
  canCreate: boolean;
  isCreating: boolean;
  setServiceName: (serviceName: APIKeyServiceNames) => void;
  setName: (name: string) => void;
  setScopes: (scopes: Set<TAPIKeyScope>) => void;
  setExpiresAt: (expiresAt: string | undefined) => void;
  setIsExpirationComplete: (isComplete: boolean) => void;
  handleCreate: () => Promise<void>;
  /** Restore every field to its initial state (used when the modal reopens). */
  reset: () => void;
}

/** Create-form state + create wiring for a V2 API key — Feathr API (service) or Zapier (legacy). */
export function useCreateAPIKey({
  onServiceKeyCreated,
  onLegacyKeyCreated,
  initialExpiresAt,
  reference,
}: IUseCreateAPIKeyOptions): IUseCreateAPIKey {
  const { t } = useTranslation();
  const { APIKeys } = useStore();
  const account = useAccount();
  const posthog = usePostHog();

  const [serviceName, setServiceName] = useState<APIKeyServiceNames>(APIKeyServiceNames.FeathrAPI);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Set<TAPIKeyScope>>(new Set<TAPIKeyScope>());
  const [expiresAt, setExpiresAt] = useState<string | undefined>(initialExpiresAt);
  const [isCreating, setIsCreating] = useState(false);
  // "Custom date" starts incomplete until a date is picked.
  const [isExpirationComplete, setIsExpirationComplete] = useState(true);

  // Legacy (Zapier) keys are scopeless and unexpiring — name only.
  const isLegacy = serviceName === APIKeyServiceNames.Zapier;
  const nameValidationErrors = useMemo(() => validateAPIKeyName(name), [name]);
  const createValidationErrors = useMemo(() => {
    // A legacy key's name is not the user's to choose — the API stores every
    // one of them as "Zapier" — so the form doesn't ask for it and doesn't
    // validate it. Validating a field that isn't shown would block Create with
    // an error pointing at nothing.
    if (isLegacy) {
      return [];
    }
    const errors = [...nameValidationErrors];
    if (selectedScopes.size === 0) {
      errors.push(t('Select at least one scope.'));
    }
    if (!isExpirationComplete) {
      errors.push(t('Select an expiration date.'));
    }
    return errors;
  }, [nameValidationErrors, isLegacy, selectedScopes, isExpirationComplete, t]);
  const canCreate = createValidationErrors.length === 0;

  async function handleCreate(): Promise<void> {
    if (!canCreate) {
      return;
    }

    // For a service key, service_name is the user's key name (cast: blackbox
    // types it as the enum; widen in #8547). For a legacy key it is not a name
    // at all: the API forces every legacy key to "Zapier" (it keys the
    // rate-limit bucket and the one-active-key-per-service rule off that exact
    // value), so send what will actually be stored rather than a name the
    // server discards.
    const attributes: Partial<IAPIKey> = isLegacy
      ? { kind: 'legacy', service_name: APIKeyServiceNames.Zapier }
      : {
          kind: 'service',
          scopes: selectedScopesToArray(selectedScopes),
          service_name: name.trim() as APIKeyServiceNames,
        };
    if (!isLegacy && expiresAt !== undefined) {
      attributes.expires_at = expiresAt;
    }

    const model = APIKeys.create(attributes);

    setIsCreating(true);
    await submitModelWithErrorHandling<APIKey>({
      onSuccess: (created) => {
        if (isLegacy) {
          onLegacyKeyCreated();
          return;
        }
        const secret = created.get('secret');
        if (secret) {
          const { has_expiration, expiration_days } = getExpirationAnalytics(expiresAt, reference);
          posthog?.capture('api_token_created', {
            account_id: account.id,
            scopes: selectedScopesToArray(selectedScopes),
            has_expiration,
            expiration_days,
          });
          onServiceKeyCreated(secret);
        } else {
          // Secret is shown only on create; unrecoverable if absent.
          showError(
            new Error(t('The key was created but its secret was not returned. Please try again.')),
            t,
          );
        }
      },
      operation: APIKeys.add(model),
      t,
    });
    setIsCreating(false);
  }

  // Stable for the reopen effect's deps.
  const reset = useCallback((): void => {
    setServiceName(APIKeyServiceNames.FeathrAPI);
    setName('');
    setSelectedScopes(new Set<TAPIKeyScope>());
    setExpiresAt(initialExpiresAt);
    setIsExpirationComplete(true);
  }, [initialExpiresAt]);

  return {
    canCreate,
    createValidationErrors,
    handleCreate,
    isCreating,
    isLegacy,
    name,
    nameValidationErrors,
    selectedScopes,
    serviceName,
    setExpiresAt,
    setIsExpirationComplete,
    setName,
    setScopes: setSelectedScopes,
    setServiceName,
    reset,
  };
}
